import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import "./DoctorSignup.css";
import drIsInLogo from "../../assets/dr-is-in.png";

const specialties = [
  "General Medicine",
  "Pediatrics",
  "Child Psychology",
  "Crisis Intervention",
  "Dermatology",
  "Neurology",
  "Other",
];

const countries = [
  "United States",
  "Canada",
  "United Kingdom",
  "India",
  "Australia",
  "Philippines",
  "Other",
];

const languageOptions = [
  "English", "Spanish", "French", "German", "Italian", "Portuguese",
  "Mandarin Chinese", "Cantonese", "Japanese", "Korean", "Arabic",
  "Hindi", "Urdu", "Bengali", "Punjabi", "Tagalog", "Vietnamese",
  "Russian", "Ukrainian", "Polish", "Hebrew", "Greek", "Turkish",
  "Swahili", "Haitian Creole", "American Sign Language", "Other",
];

const proficiencyOptions = ["Low", "Medium", "High", "Fluent"];

type LanguageSkill = {
  language: string;
  read: string;
  write: string;
  speak: string;
};

const blankLanguage = (): LanguageSkill => ({
  language: "",
  read: "",
  write: "",
  speak: "",
});

const DoctorSignup: React.FC = () => {
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  const [acceptedDisclaimer, setAcceptedDisclaimer] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    specialties: "",
    country: "",
    website: "",
    medical_license_number: "",
    bio: "",
    status: "Pending Review",
  });

  const [photo, setPhoto] = useState<File | null>(null);
  const [licenseFile, setLicenseFile] = useState<File | null>(null);
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [languages, setLanguages] = useState<LanguageSkill[]>([blankLanguage()]);

  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => setForm({ ...form, [e.target.name]: e.target.value });

  const updateLanguage = (index: number, field: keyof LanguageSkill, value: string) => {
    setLanguages((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const addLanguage = () => setLanguages((prev) => [...prev, blankLanguage()]);

  const removeLanguage = (index: number) => {
    setLanguages((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [blankLanguage()];
    });
  };

  const handleBack = () => navigate("/");
  const handleCancelDisclaimer = () => navigate("/registration-portals");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setMsg("");

    if (!licenseFile || !cvFile) {
      setMsg("Medical license and CV/Resume are required.");
      setSubmitting(false);
      return;
    }

    try {
      const fd = new FormData();

      Object.entries(form).forEach(([k, v]) => {
        if (v !== undefined && v !== null && String(v).trim() !== "") {
          fd.append(k, v);
        }
      });

      const languagePayload = languages
        .filter((row) => row.language.trim())
        .map((row) => ({
          language: row.language.trim(),
          read: row.read || "Low",
          write: row.write || "Low",
          speak: row.speak || "Low",
        }));

      fd.append("languages_json", JSON.stringify(languagePayload));

      if (photo) fd.append("photo", photo);
      fd.append("license_file", licenseFile);
      fd.append("cv_file", cvFile);

      await api.post("/public/doctor_applications", fd);

      setMsg("Submitted ✅ Pending admin review.");
      setForm({
        name: "",
        email: "",
        phone: "",
        specialties: "",
        country: "",
        website: "",
        medical_license_number: "",
        bio: "",
        status: "Pending Review",
      });
      setPhoto(null);
      setLicenseFile(null);
      setCvFile(null);
      setLanguages([blankLanguage()]);
    } catch (err: any) {
      setMsg(err?.response?.data?.detail || "Submit failed.");
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="doctor-signup-page">
      {!acceptedDisclaimer && (
        <div className="disclaimer-overlay" role="dialog" aria-modal="true" aria-labelledby="medical-disclaimer-title">
          <div className="disclaimer-modal">
            <div className="disclaimer-header">
              <img src={drIsInLogo} alt="Doctor Is In App" className="disclaimer-logo" />
              <h2 id="medical-disclaimer-title">Important Medical &amp; Platform Disclaimer</h2>
            </div>

            <div className="disclaimer-body">
              <p>
                <strong>Doctor Is In App™</strong> is an educational, informational, and wellness support platform. It is
                <strong> not</strong> intended to provide medical diagnosis, prescribe treatment, replace clinical judgment,
                or substitute for licensed medical care.
              </p>

              <p>
                Artificial intelligence (AI) responses are generated for educational and informational purposes only and
                may be incomplete, inaccurate, or inappropriate for an individual&apos;s specific circumstances. AI-generated
                responses must never be relied upon as medical advice, diagnosis, or treatment.
              </p>

              <p>
                <strong>
                  Advisors using Doctor Is In App™ agree that they will not use the platform to diagnose medical
                  conditions, prescribe medications, recommend specific medical treatments, or establish a physician-patient
                  relationship through the platform.
                </strong>{" "}
                Any diagnosis, prescription, or individualized medical treatment must occur outside Doctor Is In App™ and
                only in accordance with applicable laws, professional standards, and licensing requirements.
              </p>

              <p>
                Advisors agree to practice only within the scope of their professional license, certification, education,
                training, and the laws governing the jurisdictions in which they provide services.
              </p>

              <p>
                The chat, messaging, and video features within Doctor Is In App™ are intended to provide wellness education,
                guidance, support, coaching, and referral when appropriate. Their use alone does <strong>not</strong> establish
                a physician-patient or healthcare provider-patient relationship.
              </p>

              <p>
                <strong>Doctor Is In App™ is not intended for emergency situations.</strong> Anyone experiencing a medical or
                mental health emergency should immediately call <strong>911</strong> (U.S.) or their local emergency services
                or seek immediate medical attention.
              </p>

              <p>
                Always encourage individuals to consult their own qualified healthcare professional regarding diagnosis,
                treatment, medications, or other medical decisions.
              </p>

              <h3>Advisor Certification</h3>
              <p>
                By selecting <strong>I Agree</strong>, I certify that:
              </p>

              <ul className="disclaimer-list">
                <li>I understand Doctor Is In App™ is an educational and wellness support platform.</li>
                <li>
                  I will <strong>not</strong> use Doctor Is In App™ to diagnose medical conditions, prescribe medications, or
                  provide individualized medical treatment through the platform.
                </li>
                <li>
                  I will practice only within the scope of my professional license, certification, education, training, and
                  applicable laws.
                </li>
                <li>
                  I understand that AI-generated information may be incomplete or inaccurate and that I remain solely
                  responsible for my own professional judgment.
                </li>
                <li>I have read, understood, and agree to this disclaimer.</li>
              </ul>
            </div>

            <div className="disclaimer-footerPanel">
              <div className="disclaimer-small">
                By selecting <strong>I Agree</strong>, I acknowledge that I have read, understood, and accepted this disclaimer.
                <br />
                Last Updated: July 1, 2026
              </div>

              <div className="disclaimer-actions">
                <button type="button" onClick={() => setAcceptedDisclaimer(true)}>
                  I Agree
                </button>

                <button type="button" className="secondary" onClick={handleCancelDisclaimer}>
                  Cancel / Exit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="doctor-signup-card">
        <button type="button" className="doctor-signup-back" onClick={handleBack}>
          Back to Landing
        </button>

        <img className="doctor-signup-logo" src={drIsInLogo} alt="Doctor Is In" />

        <h2 className="doctor-signup-title">Advisor Registration</h2>
        <div className="doctor-signup-tagline">AI Wellness Advisor with a human touch.</div>

        <p className="doctor-signup-subtext">
          Submit your profile for admin review. Once approved, your profile will be published.
        </p>

        {msg && <div className="doctor-signup-msg">{msg}</div>}

        <form className="doctor-signup-form" onSubmit={handleSubmit}>
          <input
            name="name"
            placeholder="Full Name"
            value={form.name}
            onChange={handleChange}
            required
            autoComplete="name"
          />

          <div className="doctor-signup-row">
            <input
              name="email"
              placeholder="Email"
              type="email"
              value={form.email}
              onChange={handleChange}
              required
              autoComplete="email"
            />

            <input
              name="phone"
              placeholder="Phone / Cell #"
              type="tel"
              value={form.phone}
              onChange={handleChange}
              required
              autoComplete="tel"
              inputMode="tel"
            />
          </div>

          <div className="doctor-signup-row">
            <select name="specialties" value={form.specialties} onChange={handleChange} required>
              <option value="">Select Specialty</option>
              {specialties.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <select name="country" value={form.country} onChange={handleChange} required>
              <option value="">Select Country</option>
              {countries.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <input
            name="website"
            placeholder="Website"
            value={form.website}
            onChange={handleChange}
            autoComplete="url"
          />

          <input
            name="medical_license_number"
            placeholder="License / Certification #"
            value={form.medical_license_number}
            onChange={handleChange}
          />

          <textarea name="bio" placeholder="Bio" value={form.bio} onChange={handleChange} />

          <div className="doctor-language-section">
            <div className="upload-label" style={{ marginBottom: 8 }}>
              Language(s) Spoken
            </div>
            <div style={{ fontSize: 13, marginBottom: 10, opacity: 0.8 }}>
              Add each language and rate your Read, Write, and Speak proficiency.
            </div>

            {languages.map((row, index) => (
              <div
                key={index}
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(180px, 1.4fr) repeat(3, minmax(110px, 1fr)) auto",
                  gap: 8,
                  marginBottom: 8,
                  alignItems: "center",
                }}
              >
                <select
                  value={row.language}
                  onChange={(e) => updateLanguage(index, "language", e.target.value)}
                  aria-label={`Language ${index + 1}`}
                >
                  <option value="">Select Language</option>
                  {languageOptions.map((language) => (
                    <option key={language} value={language}>{language}</option>
                  ))}
                </select>

                {(["read", "write", "speak"] as const).map((field) => (
                  <select
                    key={field}
                    value={row[field]}
                    onChange={(e) => updateLanguage(index, field, e.target.value)}
                    aria-label={`${field} proficiency`}
                    disabled={!row.language}
                  >
                    <option value="">{field.charAt(0).toUpperCase() + field.slice(1)}</option>
                    {proficiencyOptions.map((level) => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                ))}

                <button
                  type="button"
                  className="secondary"
                  onClick={() => removeLanguage(index)}
                  disabled={languages.length === 1 && !row.language}
                  title="Remove language"
                >
                  Remove
                </button>
              </div>
            ))}

            <button type="button" className="secondary" onClick={addLanguage}>
              + Add Language
            </button>
          </div>

          <input type="hidden" name="status" value={form.status} readOnly />

          <div className="doctor-upload-grid">
            <div className="upload-block">
              <label className="upload-label">Upload Photo (optional)</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setPhoto(e.target.files?.[0] || null)}
              />
            </div>

            <div className="upload-block">
              <label className="upload-label">License / Certification (PDF/JPG/PNG) *</label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                required
                onChange={(e) => setLicenseFile(e.target.files?.[0] || null)}
              />

              <label className="upload-label" style={{ marginTop: 10 }}>
                CV / Resume (PDF/DOC/DOCX) *
              </label>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                required
                onChange={(e) => setCvFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>

          <button className="doctor-signup-submit" type="submit" disabled={submitting}>
            {submitting ? "Submitting..." : "Submit for Review"}
          </button>
        </form>

        <div className="doctor-signup-footer">© {year} Doctor Is In App. All rights reserved.</div>
      </div>
    </div>
  );
};

export default DoctorSignup;
